"use client";

import React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { hasHTMLTags } from "@/helpers/has-html-tag";

const headingVariants = cva("scroll-m-20  tracking-tight text-balance", {
  variants: {
    weight: {
      light: "font-light",
      regular: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
    color: {
      default: "text-inherit",
      muted: "text-muted-foreground",
      primary: "text-primary",
      secondary: "text-secondary",
      accent: "text-accent",
      destructive: "text-destructive",
    },
    wrap: {
      wrap: "whitespace-normal",
      nowrap: "whitespace-nowrap",
      pretty: "text-pretty",
      balance: "text-balance",
    },
    truncate: {
      true: "truncate block",
      false: "",
    },
    as: {
      h1: "text-4xl font-extrabold",
      h2: "text-3xl",
      h3: "text-2xl",
      h4: "text-xl",
      h5: "text-lg",
      h6: "text-base",
    },
  },
  defaultVariants: {
    weight: "semibold",
    color: "default",
    wrap: "wrap",
    as: "h1",
  },
});

export interface HeadingProps
  extends
    Omit<useRender.ComponentProps<"h1">, "color" | "wrap">,
    Omit<VariantProps<typeof headingVariants>, "as"> {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  forceAs?: boolean;
}

function Heading({
  className,
  as = "h1",
  weight,
  align,
  color,
  wrap,
  truncate,
  render,
  children,
  forceAs,
  ...props
}: HeadingProps) {
  const isHtmlString = typeof children === "string" && hasHTMLTags(children);
  const contentProps = isHtmlString
    ? { dangerouslySetInnerHTML: { __html: children } }
    : { children };

  const defaultTagName = forceAs ? as : isHtmlString ? "div" : as;

  const internalRef = React.useRef<HTMLElement | null>(null);

  const defaultProps: useRender.ElementProps<"h1"> = {
    className: cn(
      headingVariants({
        weight,
        align,
        color,
        wrap,
        truncate,
        as,
      }),
      className,
    ),
    ...contentProps,
  };

  const element = useRender({
    defaultTagName: defaultTagName,
    ref: internalRef,
    props: mergeProps<"h1">(defaultProps, props),
    render: render,
    state: {
      slot: "heading",
      weight,
      as,
    },
  });

  return element;
}

export { Heading, headingVariants };
